import { SVGAttributes } from 'react';

import { getBrandDomain } from 'utils/getBrandDomain';

export type BaseSvgProps = (SVGAttributes<SVGSVGElement> & { url: string | undefined }) | null | undefined;

export type SvgProps = Promise<BaseSvgProps | null>;

const _getBrandLogoSvg = async (iconName: string | null | undefined = ''): SvgProps => {
  if (!iconName) return null;

  const brandName = getBrandDomain();

  try {
    const module = await import(`../assets/icons/${brandName}/${iconName.trim()}.svg`);
    return { url: module.default };
  } catch (error) {
    console.warn(error);
    return null;
  }
};

const _getSvg = async (iconName: string | null | undefined = ''): SvgProps => {
  if (!iconName) return null;

  try {
    // Dynamically import the SVG file as a React component
    const module = await import(`../assets/icons/${iconName.trim()}.svg`);

    return { url: module.default };
  } catch (error) {
    console.warn(error);
    return null;
  }
};

const _getSvgByName = (iconName: string | null | undefined): string | undefined => {
  if (!iconName) return undefined;

  return iconName;
};

const svgIcons = {
  Apple: () => _getSvg('apple'),
  ArrowBack: () => _getSvg('arrow-back'),
  arrowCheck: () => _getSvg('check'),
  arrowDown: () => _getSvg('arrow-down'),
  ArrowRight: () => _getSvg('arrow-right'),
  ArrowToRight: () => _getSvg('arrow-to-right'),
  arrowUp: () => _getSvg('arrow-up'),
  arrowUpDown: () => _getSvg('arrow-upDown'),
  Asset: (asset: string | null | undefined) => _getSvgByName(asset),
  Avatar: () => _getSvg('avatar'),
  AvatarUniversal: () => _getSvg('avatar-universal'),
  AssetDefault: () => _getSvg('asset-default'),
  Back: () => _getSvg('arrow-back'),
  Bank: () => _getSvg('bank'),
  Bell: () => _getSvg('bell'),
  Calendar: () => _getSvg('calendar'),
  Candle: () => _getSvg('candle'),
  Card: () => _getSvg('card'),
  CardIco: () => _getSvg('card-ico'),
  ChartIcon: () => _getSvg('chart'),
  chartIndicators: () => _getSvg('chartIndicators'),
  chartType: () => _getSvg('chartType'),
  Check: () => _getSvg('check'),
  ChevronDown: () => _getSvg('chevron-down'),
  ChevronDownThin: () => _getSvg('chevron-down-thin'),
  ChevronDownFill: () => _getSvg('chevron-down-fill'),
  Clear: () => _getSvg('clear'),
  Close: () => _getSvg('close'),
  ClosePartially: () => _getSvg('close-partially'),
  CloseCompletely: () => _getSvg('close-completely'),
  ConnectionOffline: () => _getSvg('connection-offline'),
  CopyIcon: () => _getSvg('copy-icon'),
  CopyIconCopied: () => _getSvg('copy-icon-copied'),
  Draw: () => _getSvg('draw'),
  Delete: () => _getSvg('delete'),
  DerivativePositionClose: () => _getSvg('derivative-position-close'),
  DerivativePositionClosePartially: () => _getSvg('derivative-position-close-partially'),
  DerivativePositionChartUp: () => _getSvg('derivative-position-chart-up'),
  Done: () => _getSvg('done'),
  dropDownArrow: () => _getSvg('dropDown-arrow'),
  Edit: () => _getSvg('edit'),
  Error: () => _getSvg('error'),
  Eye: () => _getSvg('eye'),
  exchangeHistoryIcon: () => _getSvg('exchangeHistory'),
  ExclamationMark: () => _getSvg('exclamation-mark'),
  exitFullScreen: () => _getSvg('exitFullScreen'),
  Export: () => _getSvg('export'),
  Favorite: () => _getSvg('favorite'),
  FileCsv: () => _getSvg('file-csv'),
  FilePdf: () => _getSvg('file-pdf'),
  FullScreen: () => _getSvg('full-screen'),
  FullScreenDisabledIcon: () => _getSvg('fullscreen-disabled'),
  FullScreenEnabledIcon: () => _getSvg('fullscreen-enabled'),
  Flag: (countryCode: string | null | undefined) => _getSvg(`flag-${countryCode}`),
  Google: () => _getSvg('google'),
  History: () => _getSvg('history'),
  Hot: () => _getSvg('hot'),
  // Icon: (asset: string | null | undefined) => _getSvg(asset),
  Indicators: () => _getSvg('indicators'),
  Info: () => _getSvg('info'),
  InfoIco: () => _getSvg('infoIco'),
  InfoIcoInner: () => _getSvg('info-ico-blue'),
  ListIcon: () => _getSvg('list'),
  LinkExternal: () => _getSvg('link-external'),
  LockIconInner: () => _getSvg('lockIcon'),
  Logo: () => _getBrandLogoSvg('logo-light'),
  LogoCard: () => _getBrandLogoSvg('logo-card'),
  LogoMob: () => _getBrandLogoSvg('logo-light-mob'),
  LogoMini: () => _getSvg('logo-light-mini'),
  LogoDark: () => _getBrandLogoSvg('logo-dark'),
  masterCardIcon: () => _getSvg('master-card'),
  Menu: () => _getSvg('menu'),
  MenuIndicator: () => _getSvg('menu-indicator'),
  MenuAbout: () => _getSvg('menu-about'),
  MenuAboutActive: () => _getSvg('menu-about-active'),
  MenuHelp: () => _getSvg('menu-help'),
  MenuHelpActive: () => _getSvg('menu-help-active'),
  MenuHome: () => _getSvg('menu-home'),
  MenuInfo: () => _getSvg('menu-info'),
  MenuInfoActive: () => _getSvg('menu-info-active'),
  MenuLock: () => _getSvg('menu-icon-lock'),
  MenuLockActive: () => _getSvg('menu-icon-lock-active'),
  MenuNotifications: () => _getSvg('menu-notifications'),
  MenuNotificationsActive: () => _getSvg('menu-notifications-active'),
  MenuOrders: () => _getSvg('menu-orders'),
  MenuOrdersActive: () => _getSvg('menu-orders-active'),
  MenuReferal: () => _getSvg('menu-referal'),
  MenuReferalActive: () => _getSvg('menu-referal-active'),
  MenuRewards: () => _getSvg('menu-rewards'),
  MenuRewardsActive: () => _getSvg('menu-rewards-active'),
  MenuSecurity: () => _getSvg('menu-security'),
  MenuSecurityActive: () => _getSvg('menu-security-active'),
  MenuSettings: () => _getSvg('menu-settings'),
  MenuSettingsActive: () => _getSvg('menu-settings-active'),
  MenuStatement: () => _getSvg('menu-statement'),
  MenuStatementActive: () => _getSvg('menu-statement-active'),
  MenuWithdrawalAddress: () => _getSvg('menu-withdrawal-address'),
  MenuWithdrawalAddressActive: () => _getSvg('menu-withdrawal-address-active'),
  Minus: () => _getSvg('minus'),
  newBorderGreen: () => _getSvg('new-border-green'),
  newBorderGreenMobile: () => _getSvg('new-border-greenP'),
  newBorderRed: () => _getSvg('new-border-red'),
  newBorderRedMobile: () => _getSvg('new-border-redP'),
  buyBorderSingle: () => _getSvg('buy-border-single'),
  sellBorderSingle: () => _getSvg('sell-border-single'),
  negativeChartIcon: () => _getSvg('negative-icon'),
  positiveChartIcon: () => _getSvg('positive-icon'),
  buyBorderDesktop: () => _getSvg('buyBorderDesktop'),
  sellBorderDesktop: () => _getSvg('sellBorderDesktop'),
  buyBorderMobile: () => _getSvg('buyBorderMobile'),
  sellBorderMobile: () => _getSvg('sellBorderMobile'),
  NotFoundOrders: () => _getSvg('arrow-trades'),
  NotFoundTrades: () => _getSvg('clock'),
  Notifications: () => _getSvg('notifications'),
  OrderBookBuy: () => _getSvg('order-book-buy'),
  OrderBookDefault: () => _getSvg('order-book-default'),
  OrderBookSell: () => _getSvg('order-book-sell'),
  OpenPositions: () => _getSvg('open-positions'),
  Pause: () => _getSvg('pause'),
  Paypal: () => _getSvg('paypal'),
  penIcon: () => _getSvg('pen-icon'),
  PersonaIco: () => _getSvg('profileIco'),
  Play: () => _getSvg('play'),
  Plus: () => _getSvg('plus'),
  PriceChangeDown: () => _getSvg('price-change-down'),
  PriceChangeTriangle: () => _getSvg('price-change-poligon'),
  PriceChangeUp: () => _getSvg('price-change-up'),
  QrCode: () => _getSvg('qr-scaner'),
  Receive: () => _getSvg('receive'),
  ReceiveIco: () => _getSvg('receive-ico'),
  ReceiveIcon: () => _getSvg('receive-icon'),
  Refresh: () => _getSvg('refresh'),
  Reject: () => _getSvg('reject'),
  reversIcon: () => _getSvg('revers-icon'),
  transferReverseIcon: () => _getSvg('transfer-reverse-icon'),
  Search: () => _getSvg('search'),
  Settings: () => _getSvg('settings'),
  Send: () => _getSvg('upload'),
  SendIcon: () => _getSvg('send-icon'),
  ShareIcon: () => _getSvg('share-icon'),
  SignOut: () => _getSvg('sign-out'),
  SplitScreenIcon: () => _getSvg('fullscreen-options'),
  statusBar: () => _getSvg('StatusBar'),
  Swift: () => _getSvg('swift'),
  timerIcon: () => _getSvg('timer'),
  Trade: () => _getSvg('arrow-trades'),
  Transfer: () => _getSvg('transfer'),
  Trash: () => _getSvg('trash'),
  Triangle: () => _getSvg('poligon'),
  TransactionEarnReward: () => _getSvg('TransactionEarnReward'),
  TransactionBonus: () => _getSvg('TransactionBonus'),
  TransactionTransfer: () => _getSvg('TransactionTransfer'),
  ThemeDark: () => _getSvg('theme-dark'),
  Unfavorite: () => _getSvg('unfavorite'),
  unionIcon: () => _getSvg('union'),
  UpLarge: () => _getSvg('up-l'),
  Upload: () => _getSvg('trans-upload'),
  Verified: () => _getSvg('verified'),
  VerifiedInner: () => _getSvg('verified-inner'),
  VerticalMenu: () => _getSvg('vertical-menu'),
  visaIcon: () => _getSvg('visa'),
  visaCardIcon: () => _getSvg('card-visa'),
  Warning: () => _getSvg('warning'),
  WarningIco: () => _getSvg('warning-ico'),
  TransferShape: () => _getSvg('transfer-shape'),
  WalletPortfolioIcon: () => _getSvg('portfolio-icon'),
  WalletMainIcon: () => _getSvg('main-icon'),
  WalletFuturesIcon: () => _getSvg('futures-icon'),
  FuturesIcon: () => _getSvg('futures-icon-simple'),
  WalletEarnsIcon: () => _getSvg('earns-icon'),
  WalletStocksIcon: () => _getSvg('stocks-icon'),
  TransferIcon: () => _getSvg('transfer-icon'),
  TradeIcon: () => _getSvg('trade-icon'),
  StocksIcon: () => _getSvg('stocks-icon-simple'),
  WalletNotFound: () => _getSvg('icon-wallet'),
  MoreIcon: () => _getSvg('more-icon'),

  Web3Address: () => _getSvg('web3-address'),
  Web3Chain: () => _getSvg('web3-chain'),
  WalletConnect: () => _getSvg('walletConnect'),

  ChartArea: () => _getSvg('chart-area'),
  ChartBars: () => _getSvg('chart-bars'),
  ChartBaseline: () => _getSvg('chart-baseline'),
  ChartCandles: () => _getSvg('chart-candles'),
  ChartColumns: () => _getSvg('chart-columns'),
  ChartHeikinAshi: () => _getSvg('chart-heikin-ashi'),
  ChartHighLow: () => _getSvg('chart-high-low'),
  ChartHlcArea: () => _getSvg('chart-hlc-area'),
  ChartHollowCandles: () => _getSvg('chart-hollow-canles'),
  ChartLine: () => _getSvg('chart-line'),
  ChartStepLine: () => _getSvg('chart-step-line'),
  ChartLineWithMarkers: () => _getSvg('chart-line-with-markers'),
  LockIcon: () => _getSvg('lock-icon'),
  VideoIcon: () => _getSvg('video'),
  CryptoPayX: () => _getSvg('cryptopayx'),
  ExpandIcon: () => _getSvg('expand-icon'),
  EarnIcon: () => _getSvg('earn-icon'),
  FlagIcon: () => _getSvg('flag-icon'),
  ExclamationMarkIcon: () => _getSvg('exclamation-mark-ico'),
  EyeOpened: () => _getSvg('eye-opened'),
  EyeClosed: () => _getSvg('eye-closed'),
  Filter: () => _getSvg('filter'),
  FilterDown: () => _getSvg('filter-down'),
  FilterDownAZ: () => _getSvg('filter-down-az'),
  FilterDownZA: () => _getSvg('filter-down-za'),
  Success: () => _getSvg('success'),
  XboToken: () => _getSvg('xbo-token'),
  XboCard: () => _getSvg('xbo-card'),
  XboCardDeskActive: () => _getSvg('xbo-card-desk-active'),
  XboCardMobActive: () => _getSvg('xbo-card-mob-active'),
  PaymentCard: () => _getSvg('payment-card'),
};

export default svgIcons;
